import { Preferences } from "@capacitor/preferences";

/**
 * Key-value persistence.
 *
 * Capacitor's Preferences plugin is used on every platform, not just native: on the
 * web it falls back to `localStorage`, so there is one code path instead of two. The
 * cost is that reads are async — callers that need a synchronous value (the API
 * client's `Authorization` header) cache it in memory at boot.
 *
 * Every call is wrapped: storage can be unavailable (Safari private mode), and losing
 * a preference must never take the app down with it.
 */

export const StorageKey = {
  Token: "elyxeer.token",
  Locale: "elyxeer.locale",
  ColorScheme: "elyxeer.color-scheme",
  // Frozen string: it addresses the FCM token an installed build already registered
  // with the API, so renaming it orphans that device's registration on upgrade.
  ChatDeviceRegistration: "elyxeer.chat-device-registration",
  // Set when the member dismisses the notification centre's permission card, so the
  // ask is offered once rather than on every visit.
  NotificationPromptDismissed: "elyxeer.notification-prompt-dismissed",
  AnalyticsQueue: "elyxeer.analytics-queue",
  AnalyticsSession: "elyxeer.analytics-session",
} as const;

export type StorageKey = (typeof StorageKey)[keyof typeof StorageKey];

export async function getItem(key: StorageKey): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key });
    return value;
  } catch {
    return null;
  }
}

export async function setItem(key: StorageKey, value: string): Promise<void> {
  try {
    await Preferences.set({ key, value });
  } catch {
    // Storage may be unavailable; the in-memory session still works for this run.
  }
}

export async function removeItem(key: StorageKey): Promise<void> {
  try {
    await Preferences.remove({ key });
  } catch {
    // Ignore — see above.
  }
}
